import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { GenericPageError } from '@components/core-ui/GenericPageError';
import { Wrapper } from '@modals/Admin/AdminBackupFilesModal/styles/Wrapper';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useAPI } from '@providers/useAPI';
import { useModalContext } from '@providers/ModalProvider';
import { JobsAdminService } from 'app/API';
import { useEffect, useState } from 'react';

type TProps = {
  onCancel: () => void;
  onSuccess: () => void;
  id: string;
};

export const AdminBackupFilesModal = ({ onCancel, onSuccess, id }: TProps) => {
  const { call } = useAPI();
  const { closeModal, openModal } = useModalContext();
  const [files, setFiles] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [mixdownJobId, setMixdownJobId] = useState<string>();

  const onSubmit = async () => {
    setIsLoading(true);
    const mixdownJobId = await call(JobsAdminService.adminMixdownJobBackupFiles({ jobId: id }));
    setMixdownJobId(mixdownJobId);
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const files = await call(JobsAdminService.adminGetJobBackupFiles({ jobId: id }));
      setFiles(files);
      setIsLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (!mixdownJobId) {
      return;
    }
    const interval = setInterval(async () => {
      const status = await call(JobsAdminService.adminGetMixdownJobBackupFilesStatus({ mixdownId: mixdownJobId }));
      if (status.returnvalue) {
        clearInterval(interval);
        closeModal();
        onSuccess();
      }
      if (status.failedReason) {
        clearInterval(interval);
        openModal(ModalNamesEnum.GenericErrorModal, {
          message: 'Backup Files Processing Error',
        });
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [mixdownJobId]);

  if (!isLoading && !files.length) {
    return (
      <Wrapper>
        <ModalWrapper width="400px">
          <ModalTitle>Attach Backup Files</ModalTitle>

          <GenericPageError message="No files to attach" />

          <div>
            <Button onClick={onCancel}>Ok</Button>
          </div>
        </ModalWrapper>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <ModalWrapper width="400px">
        {isLoading ? <AbsoluteSpinner overlay={true} /> : null}
        <ModalTitle>Attach Backup Files</ModalTitle>

        <div className="files">
          {files.map((file, index) => (
            <div key={`${file}-${index}`} className="file">
              {file}
            </div>
          ))}
        </div>

        <div className="flex gap-2">
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button disabled={isLoading} onClick={onSubmit}>
            Attach
          </Button>
        </div>
      </ModalWrapper>
    </Wrapper>
  );
};
